import {getFormValues} from 'redux-form';
import {scopedHandler} from 'utils/redux';
import ns from './namespace';
import initState from './state';
import * as actions from './actions';
import * as effects from './effects';
import * as rootSelectors from 'modules/common/selectors';
import {decorateHandler as lifecycle} from 'fragments/lifecycle';
import {
	formatCallCampaignCreateOutput,
	formatCallCampaignUpdateOutput,
	formatQueryAreas,
} from './utils';

const handler = scopedHandler(ns, (state = initState, fullState, {type, payload}) => {
	switch (type) {
		case actions.initialize.type: {
			return [state, effects.initialize()];
		}

		case actions.destroy.type: {
			return [initState, null];
		}

		case actions.changePage.type: {
			const newState = {
				...state,
				buildingsLoading: true,
				buildingsQuery: {...state.buildingsQuery, _page: payload},
			};

			return [newState, effects.updateBuildings()];
		}

		case actions.searchBuildings.type: {
			const newState = {
				...state,
				buildingsLoading: true,
				buildingsQuery: {
					...state.buildingsQuery,
					_page: 1,
					manufacturingYearEnd: payload.manufacturingYearEnd,
					areas: payload.areas,
					tagIds: payload.tagIds,
					dismissPrompt: true,
				},
			};

			return [newState, effects.updateBuildings()];
		}

		case actions.recheckQuery.type: {
			return [state, effects.recheckQuery()];
		}

		case actions.openAreasSelector.type: {
			return [{...state, areasSelectorOpen: true}, effects.initAreasMap()];
		}

		case actions.closeAreasSelector.type: {
			const newState = {
				...state,
				areasSelectorOpen: false,
				areasSelectorSearch: '',
			};
			return [newState, null];
		}

		case actions.setAreasSelectorType.type: {
			return [state, effects.setAreasMapType(payload)];
		}

		case actions.setAreasSelectorSearch.type: {
			return [{...state, areasSelectorSearch: payload}, null];
		}

		case actions.openAreasSelectorSuggestion.type: {
			return [state, effects.openAreasSelectorSuggestion(payload)];
		}

		case actions.setActiveCallCampaign.type: {
			return [state, effects.setActiveCallCampaign(payload)];
		}

		case actions.createCallCampaign.type: {
			const campaignCreatorFormVals = getFormValues('callCampaignCreatorForm')(fullState);
			const buildingsSearchFormVals = getFormValues('buildingsSearchForm')(fullState);

			const newCampaign = formatCallCampaignCreateOutput({
				title: campaignCreatorFormVals.title,
				buildingsQuery: buildingsSearchFormVals,
				userId: rootSelectors.user(fullState).id,
			});

			return [{...state, processing: true}, effects.createCallCampaign(newCampaign)];
		}

		case actions.updateCallCampaign.type: {
			const buildingsSearchFormVals = getFormValues('buildingsSearchForm')(fullState);

			const updatedCampaign = formatCallCampaignUpdateOutput({
				id: state.buildingsQuery.campaign,
				buildingsQuery: buildingsSearchFormVals,
			});

			return [{...state, processing: true}, effects.updateCallCampaign(updatedCampaign)];
		}

		case actions.removeCallCampaign.type: {
			return [{...state, processing: true}, effects.removeCallCampaign()];
		}

		case actions.openCallCampaignCreator.type: {
			return [{...state, callCampaignCreatorOpen: true}, null];
		}

		case actions.closeCallCampaignCreator.type: {
			return [{...state, callCampaignCreatorOpen: false}, null];
		}

		case actions.navigateToBuilding.type: {
			const newState = {
				...state,
				lastVisitedBuilding: payload,
			};

			return [newState, effects.navigateToBuilding(payload)];
		}

		case actions.updateBuilding.type: {
			return [
				{...state, lastVisitedBuildingLoading: true},
				effects.updateBuilding(payload),
			];
		}

		case actions._setBuildings.type: {
			const newState = {
				...state,
				buildings: payload.data,
				buildingsPagination: payload.pagination,
				buildingsLoading: false,
			};

			return [newState, null];
		}

		case actions._updateBuildingsQuery.type: {
			const newState = {
				...state,
				buildingsLoading: true,
				buildingsQuery: {...state.buildingsQuery, ...payload},
			};

			return [newState, null];
		}

		case actions._setCallCampaigns.type: {
			return [{...state, callCampaigns: payload}, null];
		}

		case actions._setActiveCallCampaign.type: {
			const newState = {
				...state,
				buildingsLoading: true,
				buildingsQuery: {
					_page: 1,
					manufacturingYearEnd: payload.query.manufacturingYearEnd,
					areas: formatQueryAreas(payload.query.areas),
					campaign: payload.id,
					dismissPrompt: true,
				},
			};

			return [newState, null];
		}

		case actions._clearActiveCallCampaign.type: {
			const newState = {
				...state,
				buildingsQuery: {
					...state.buildingsQuery,
					campaign: null,
				},
			};

			return [newState, null];
		}

		case actions._createCallCampaign.type: {
			const newState = {
				...state,
				processing: false,
				callCampaignCreatorOpen: false,
				callCampaigns: [...state.callCampaigns, payload],
			};

			return [newState, null];
		}

		case actions._removeCallCampaign.type: {
			const callCampaigns = state.callCampaigns.filter(c => c.id !== payload);

			const newState = {
				...state,
				processing: false,
				callCampaigns,
			};

			return [newState, null];
		}

		case actions._updateCallCampaign.type: {
			const callCampaigns = state.callCampaigns.map(c => {
				if (c.id === payload.id) return payload;
				return c;
			});

			const newState = {
				...state,
				processing: false,
				callCampaigns,
			};

			return [newState, null];
		}

		case actions._updateBuilding.type: {
			const buildings = state.buildings.map(b => {
				if (b.id === payload.id) return payload;
				return b;
			});

			const newState = {
				...state,
				buildings,
				lastVisitedBuildingLoading: false,
			};

			return [newState, null];
		}

		case actions._opOk.type: {
			return [{...state, processing: false}, null];
		}

		case actions._opFailed.type: {
			return [{...state, processing: false}, null];
		}

		case actions.getAvailableTags.type: {
			return [state, effects.getAvailableTags()];
		}

		case actions._getAvailableTags.type: {
			return [
				{
					...state,
					availableTags: payload,
				},
				null,
			];
		}

		default:
			return [state, null];
	}
});

export default lifecycle({
	namespace: ns,
	initializeType: actions.initialize.type,
	destroyType: actions.destroy.type,
})(handler);
